import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';

import { Box, Button, CardContent, FormControl, MenuItem, Select, TextField } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { sortBy, uniq } from 'lodash';
import { CUSTOMER_PRACTITIONER_ASSIGNED } from 'src/constants/reactQuery.keys';
import { PORTRAIT_LEGACY } from 'src/constants/general.constants';
import { SmallTitle, Card } from '../../common/card';
import { useStyles } from './index.style';

import { RootState, dispatch } from '../../../rematch';
import { IPractitioner } from '../../../interfaces/IPractitioner';
import IServiceVisit from '../../../interfaces/IServiceVisits';
import { ShortMultipleSkeleton } from '../../common/LoadingSkeleton';
import { hasAccessTo } from '../../../utils/auth.utils';
import { READ_USER_GROUP, UPDATE_CUSTOMER_CHANGE_PRACTITIONER } from '../../../constants/actions.constants';
import { CONTACT_INFO, useCustomContactInfo } from '../../../hooks/queries/useCustomContactInfo';
import MultipleSelector from '../../common/Custom/MultipleSelector';
import { useUserGroupInfinity } from '../../../hooks/queries/useUserGroups';
import { usePractitionersDropdown } from '../../../hooks/queries/usePractitionersDropdown';
import {
  useCustomerPractitionersAssigned,
  useUpdateCustomerPractitionersAssignedMutation,
} from '../../../hooks/queries/customers/customerPractitioners';
import { useMedspaPractitioners } from '../../../hooks/queries/usePractitioners';
import IMedspa from '../../../interfaces/IMedspa.interface';
import { EHRAutocompleteSelect, IAutocompleteSelectOption } from '../../ui/v1';

const buttonStyle = {
  color: '#000000',
  backgroundColor: '#e7eeed',
  border: 'solid 1px rgba(0, 0, 0, 0.12)',
  height: '34px',
  marginRight: 0,
};

const customerPractitionersSchema = yup.object().shape({
  customerPractitioners: yup.array(),
  medspaAdmins: yup.array(),
});

const AssignedPractitioner = () => {
  const classes = useStyles();
  const { patientId }: any = useParams();
  const queryClient = useQueryClient();

  const permissions = useSelector(({ auth }: RootState) => auth.permissions);
  const canReadUserGroups = hasAccessTo(READ_USER_GROUP, permissions);
  const { serviceVisits } = useSelector(({ fullServiceHistory }: RootState) => fullServiceHistory);

  const { data: practitioners, isLoading: isLoadingPractitioners } = useMedspaPractitioners(
    {},
    true,
    () => 'v3/practitioners'
  );

  const { data: contactInfo, isLoading: isLoadingContactInfo } = useCustomContactInfo(patientId?.toString());
  const { id, practitioner } = contactInfo || {};

  const [medspaKey, setMedspaKey] = useState<number>(0);
  const [medspaSearch, setMedspaSearch] = useState<string>('');
  const [medspaParams, setMedspaParams] = useState<Record<string, any>>({
    search: '',
    sort: 'created_at',
    direction: 'desc',
    page: 1,
    limit: 30,
  });
  const {
    data: { userGroupList: medspas = [] },
    isLoading: isLoadingMedspaAdmins,
  } = useUserGroupInfinity(medspaParams, 0, canReadUserGroups && medspaSearch.length > 2);

  useEffect(() => {
    setMedspaParams({ ...medspaParams, ...{ search: medspaSearch, sort: 'name', direction: 'asc' } });
  }, [medspaSearch]);

  const { data: practitionerList } = usePractitionersDropdown();

  const {
    data: practitionersAssigned,
    isLoading: isLoadingPractitionersAssigned,
    isFetching: isFetchingPractitionersAssigned,
  } = useCustomerPractitionersAssigned(+patientId);
  const updatePractitionersAssignedMutation = useUpdateCustomerPractitionersAssignedMutation(+patientId);
  const sharePractitionerLoaded = !isLoadingPractitionersAssigned;

  const practitionerOptions = useMemo(
    () =>
      practitionerList?.map(({ userGroupName, firstName, lastName, id: practitionerId }) => ({
        name: `${firstName} ${lastName}${userGroupName ? ` - ${userGroupName}` : ''}`,
        value: practitionerId,
      })) || [],
    [medspas, practitionerList]
  );

  const { control } = useForm<FormData>({
    resolver: yupResolver(customerPractitionersSchema),
    defaultValues: { customerPractitioners: [], medspaAdmins: [] },
  });
  const [changePractitioner, setChangePractitioner] = useState(false);

  const handleChangePractitioner = () => {
    if (serviceVisits !== null && serviceVisits.some(({ opened }: IServiceVisit) => opened)) {
      dispatch({
        type: 'snackbar/enqueueSnackBar',
        payload: {
          message: 'There is a service visit currently open',
          type: 'error',
        },
      });
      return;
    }
    setChangePractitioner(!changePractitioner);
  };

  const setNewPractitioner = (event: any) => {
    dispatch({
      type: 'patient/changePractitionerRequest',
      payload: {
        practitioner_id: event.target.value,
        patientId: id,
        callback: () => {
          setChangePractitioner(false);
          // invalidate the contact to refresh the practitioner and physician
          id !== null && queryClient.invalidateQueries([CONTACT_INFO]);
          id !== null && queryClient.invalidateQueries([CUSTOMER_PRACTITIONER_ASSIGNED]);
        },
      },
    });
  };

  const onChangeAdmins = (medspaOption: IAutocompleteSelectOption | null) => {
    const practitionersInAdmin =
      practitionerList
        ?.filter(({ userGroupId }) =>
          userGroupId !== undefined && medspaOption?.value && userGroupId !== null
            ? medspaOption.value === userGroupId
            : false
        )
        .map(({ id: practitionerId }) => practitionerId) || [];

    if (practitionersInAdmin.length) {
      onChangePractitioners(uniq([...practitionersAssigned, ...practitionersInAdmin]));
    } else {
      dispatch({
        type: 'snackbar/enqueueSnackBar',
        payload: {
          message: 'The selected MedSpa has no providers',
          type: 'error',
        },
      });
    }
    setMedspaKey(medspaKey + 1);
  };

  const onChangePractitioners = (newVal: any) => {
    updatePractitionersAssignedMutation.mutate({
      practitionerIds: newVal,
    });
  };

  const fullyLoaded = !isLoadingContactInfo && !isLoadingPractitioners && !isLoadingPractitionersAssigned;

  return (
    <Card>
      {!fullyLoaded ? (
        <Card>
          <CardContent style={{ padding: '12px 15px' }}>
            <ShortMultipleSkeleton length={2} />
          </CardContent>
        </Card>
      ) : (
        <CardContent>
          <SmallTitle title="Assigned practitioner" style={{ marginTop: '0', marginBottom: '18px' }} />
          <div className={classes.selectedPractitioner}>
            Default Practitioner: {practitioner ? `${practitioner.providerLabeling}` : 'None'}
            {hasAccessTo(UPDATE_CUSTOMER_CHANGE_PRACTITIONER, permissions) && (
              <Button style={buttonStyle} onClick={() => handleChangePractitioner()}>
                {changePractitioner ? 'Cancel' : 'Change'}
              </Button>
            )}
          </div>
          {changePractitioner && (
            <FormControl variant="outlined" className={classes.practitionerDropdown}>
              <Select
                className={classes.dropdown}
                value={practitioner?.id}
                onChange={(item) => {
                  setNewPractitioner(item);
                }}
              >
                {sortBy(practitioners || [], (provider) => provider.lastName).map(
                  ({ firstName, lastName, licenses, id: practitionerId }: IPractitioner) => (
                    <MenuItem key={practitionerId} value={practitionerId}>
                      {lastName} {firstName}. {licenses}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          )}

          {hasAccessTo(UPDATE_CUSTOMER_CHANGE_PRACTITIONER, permissions) &&
            canReadUserGroups &&
            sharePractitionerLoaded && (
            <Box>
              <Box paddingTop="10px">
                <EHRAutocompleteSelect
                  dataCy="autocompleteselect-medspa"
                  options={
                    medspaSearch.length > 2
                      ? medspas.map((m: IMedspa) => ({
                        name: `Add providers of ${m.name}`,
                        value: m.id,
                      }))
                      : []
                  }
                  disableCloseOnSelect={false}
                  openOnFocus
                  clearOnEscape
                  label="Add providers by MedSpa"
                  value={null}
                  blurOnSelect
                  onChange={onChangeAdmins}
                  inputValue={medspaSearch}
                  onChangeDebounceSearch={(newInputValue) => {
                    setMedspaSearch(newInputValue);
                  }}
                  loading={isLoadingMedspaAdmins}
                  getOptionLabel={(option: IAutocompleteSelectOption) => `${option.name}`}
                  fullWidth
                  renderInput={(params) => (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <TextField {...params} placeholder="Select MedSpa" variant="outlined" />
                  )}
                  key={medspaKey}
                  noOptionsText={medspaSearch.length > 2 ? 'No Options' : 'Type at least 3 characters to search'}
                />
              </Box>
              <Box paddingTop="10px">
                {updatePractitionersAssignedMutation.isLoading || isLoadingPractitionersAssigned ? (
                  <ShortMultipleSkeleton length={2} />
                ) : (
                  <MultipleSelector
                    autocomplete
                    disabled={
                      practitioner?.practitionerRole === PORTRAIT_LEGACY ||
                        updatePractitionersAssignedMutation.isLoading ||
                        isFetchingPractitionersAssigned
                    }
                    label="Practitioners"
                    name="customerPractitioners"
                    control={control}
                    value={practitionersAssigned}
                    onChange={onChangePractitioners}
                    errors={null}
                    options={practitionerOptions}
                  />
                )}
              </Box>
            </Box>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default AssignedPractitioner;
